import {
    AssetProcessingStateId,
    type Column,
    type CreateWorksheetFormRequestBody,
    type ExistingElements,
    type Grid,
    type SelectedOption,
    type SubColumn,
    type SubRow,
    type WorksheetAsset as Asset,
    WorksheetAnswerStatus,
    WorksheetAssetImageType,
    WorksheetAssetType,
    type WorksheetFormElement,
    type WorksheetFormElementOptions,
    type WorksheetFormGetResponse,
    type WorksheetGridCellAnswer,
    WorksheetGroupStatus,
    type WorksheetUsersAccount,
} from '@packages/clients';
import { type DraggableFormElement, type FormElementFile } from './form';
import { TechlineStatusCode } from './techline';

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { WorksheetGroupStatus };

export enum WorkSheetStatusEnum {
    All = 'ALL',
    Pending = 'PENDING',
    Pilot = 'PILOT',
    Published = 'PUBLISHED',
    Archived = 'ARCHIVED',
    AddVersion = 'ADD VERSION',
}

export enum WorksheetCaseTypeStatus {
    Active = 'ACTIVE',
}

export interface CaseType {
    caseTypeId: string;
    northstarId: string;
    caseType: string;
}

export interface CaseTypesResponse {
    casetypes: CaseType[];
}
/**
 * @deprecated Use {@link WorksheetFormEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface WorksheetForm {
    worksheetId?: string;
    worksheetGroupId?: string;
    caseTypeId: string;
    caseType?: string;
    northstarId: string;
    groupStatus: string;
    worksheetGroupName: string;
    elements: WorksheetFormElement[];
    description?: string;
    displayname: string;
    worksheetStatus: string;
    version: number;
    worksheetCaseGroupId?: string;
    caseGroupName?: string;
    createdAccount?: WorksheetUsersAccount;
    lastUpdatedAccount?: WorksheetUsersAccount;
    createdTimestamp?: string;
    createdTimestampForDisplay?: string;
    lastUpdatedTimestamp?: string;
    lastUpdatedTimestampForDisplay?: string;
    worksheetSubmissionStatus?: WorksheetAnswerStatus;
    existingElements?: ExistingElements[];
    oldWorksheetElements?: WorksheetFormElement[];
}
/**
 * @deprecated Use {@link WorksheetFormElementOptionEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { WorksheetFormElementOptions };

export interface selectedOption {
    answerText: string;
    optionId: string;
    optionLabel: string;
}

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { WorksheetAssetImageType };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { WorksheetAssetType };
interface Media {
    assetImageId: string;
    assetImageTypeId: WorksheetAssetImageType;
    assetVideoId?: string;
    assetFileId?: string;
    assetVideoTypeId?: string;
    assetImageTypeIdDescription: string;
    url: string;
    contentType: string;
    sizeInBytes: number;
    sizeInBytesDescription: string;
    width: number;
    widthDescription: string;
    height: number;
    heightDescription: string;
    createdTimestamp: Date;
    createdTimestampDescription: string;
}

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export { AssetProcessingStateId };
/**
 * @deprecated Use {@link TechlineAssetEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated. Re-exported from `@packages/clients` as `WorksheetAsset`.
 */
export type { Asset };

/**
 * @deprecated Use {@link WorksheetFormElementEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { WorksheetFormElement };

/**
 * @deprecated Grid data is accessed via {@link WorksheetFormElementEntity} from `@packages/domain`. Replaced
 * by domain entity layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { Grid };
/**
 * @deprecated Use {@link WorksheetFormGridColumnEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { Column };

/**
 * @deprecated Use {@link WorksheetFormGridSubColumnEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { SubColumn };

type SubRowColumn = Omit<Column, 'subColumns'> & {
    subColumns?: [];
};

/**
 * @deprecated Use {@link WorksheetFormGridSubRowEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { SubRow };
/**
 * @deprecated Use {@link WorksheetFormElementOptionEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated.
 */
export interface WorksheetFormElementOption {
    optionId: string;
    optionValue: string;
    optionLabel: string;
    optionPlaceholder: string;
    optionImage?: string;
    isOther?: boolean;
    trgtElementIdList?: string[];
    optionDisplayOrder?: number;
}

export interface CreateAsset {
    assetDispositionId: 'WORKSHEET';
    worksheetElementId?: string;
    assetTypeId: WorksheetAssetType;
    hasOverlay: boolean;
    name: string;
    sizeInBytes?: number;
    caption?: string;
    note?: string;
}
export interface CreateOptionAsset extends Omit<CreateAsset, 'worksheetElementId'> {
    worksheetElementOptionId?: string;
}
export interface CreateOverlayAsset extends CreateAsset {
    replacesAssetId?: string;
    note?: string;
}

export type CreateAssetTypes = CreateOverlayAsset | CreateAsset | CreateOptionAsset;

export interface CreateAssets {
    assets: CreateAssetTypes[];
}

export interface CreateAssetsResonseHttpHeaders {
    'x-amz-meta-asset-id': string;
    'x-amz-meta-asset-disposition-id': string;
    'x-amz-meta-access-token': string;
    'x-amz-meta-asset-type-id': string;
    'x-amz-meta-backend-callback-base-url': string;
}

interface PresignedUpload {
    httpMethod: string;
    url: string;
    expirationTimestamp: string;
    expirationTimestampDescription: string;
    httpHeaders: CreateAssetsResonseHttpHeaders;
}

export interface AnswerPresignedUpload {
    httpMethod: string;
    url: string;
    expirationTimestamp: string;
    expirationTimestampDescription: string;
    httpHeaders: {
        'x-ms-blob-type': string;
    };
}

/**
 * @deprecated Use {@link AnswerAssetEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface AnswerAsset {
    assetId: string;
    assetTypeId: WorksheetAssetType;
    assetTypeIdDescription: string;
    assetDispositionId: 'TECHSHARE';
    assetDispositionIdDescription: string;
    assetProcessingStateId: AssetProcessingStateId;
    assetProcessingStateIdDescription: string;
    percentUploaded: number;
    percentUploadedDescription: string;
    name: string;
    importedFromSiebel: boolean;
    createdTimestamp: string;
    createdTimestampDescription: string;
}

export interface CreateAnswerAssetsResponse {
    results: {
        assets: { asset: AnswerAsset; presignedUpload: AnswerPresignedUpload }[];
        statusCode: number;
        status: string;
        message: string;
        totalRecords: string;
    };
}
export interface OverlayPresignedUpload extends Omit<PresignedUpload, 'httpHeaders'> {
    httpHeaders: {
        'x-amz-meta-asset-id': string;
    };
}
interface CreateAssetData {
    presignedUpload: PresignedUpload;
    asset: Asset;
    overlayPresignedUpload: OverlayPresignedUpload;
}
export interface CreateAssetsResponse {
    assets: CreateAssetData[];
}

interface DeleteOptionAssets {
    worksheetElementOptionId: string;
    assetId: string;
}

interface DeleteElementAssets {
    worksheetElementId: string;
    assetId: string;
}

export type DeleteAssetBody = DeleteOptionAssets | DeleteElementAssets;

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { CreateWorksheetFormRequestBody };

export interface UpdateWorksheetFormRequestBody extends CreateWorksheetFormRequestBody {
    worksheetId: string;
    worksheetGroupId: string;
    caseType: string;
    existingElements?: ExistingElements[];
    caseGroupName?: string;
}

export interface AddVersionWorksheetFormRequestBody extends UpdateWorksheetFormRequestBody {}

export interface UpdateWorksheetFormStatusBody {
    worksheetId: string;
    worksheetStatus: WorkSheetStatusEnum;
}

export interface UpdateWorksheetFormStatusResponse extends WorksheetFormGetResponse {}

/**
 * @deprecated Use {@link WorksheetFormDto} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface WorksheetFormDto extends CreateWorksheetFormRequestBody {
    worksheetId: string;
    createdTimestamp: string;
    createdTimestampForDisplay: string;
}

/**
 * @deprecated Use {@link WorksheetFormUserAccountEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { WorksheetUsersAccount };

/**
 * @deprecated Use {@link WorksheetFormExistingElementEntity} from `@packages/domain`. Replaced by domain
 * entity layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { ExistingElements };

/**
 * @deprecated Use the equivalent from `@packages/clients` directly. Re-exported for backward compatibility.
 * Retained until all consumers are migrated.
 */
export type { WorksheetFormGetResponse };

export interface DraggableWorksheetFormElement extends WorksheetFormElement {
    title: string;
    description: string;
    iconName: string;
}

export interface DraggableWorksheetFormComponent extends DraggableFormElement {}

/**
 * @deprecated Use {@link WorksheetTitleEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface Worksheets {
    worksheetId: string;
    displayName: string;
    version: number;
    displayNameWithVersion: string;
    status: WorksheetGroupStatus;
    lastUpdatedTimestamp: string;
    allowEdit: boolean;
}
export interface WorksheetsFetchObject {
    caseType: string;
    statusList: WorksheetGroupStatus[];
    lastUpdatedTimestampForDisplay: string;
    lastUpdatedTimestamp: string;
    totalCount: number;
    maxVersion: number;
    worksheets: Worksheets[];
}

export type WorksheetsFetchResponse = {
    summaryMap: Record<string, WorksheetsFetchObject>;
    totalCount: number;
};

export { WorksheetAnswerStatus };
/**
 * @deprecated Use {@link WorksheetFormSelectedOptionEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { SelectedOption };

/**
 * @deprecated Use {@link WorksheetFormGridCellAnswerEntity} from `@packages/domain`. Replaced by domain entity
 * layer. Retained until all consumers are migrated. Re-exported from `@packages/clients`.
 */
export type { WorksheetGridCellAnswer };

export interface CreateSelectedOptionAssets {
    assetTypeId: WorksheetAssetType;
    assetDispositionId: 'TECHSHARE';
    caseNumber: string;
    fileName: string;
    techlinecasestatus: TechlineStatusCode;
    restricted: boolean;
    hasOverlay: boolean;
    note: string;
    worksheetQuestionSubmissionSourceId: string;
}

export interface WorksheetListByCaseTypeBody {
    caseTypes: { caseType: string; northstarId: string }[];
    isPilotUser: boolean;
}

export interface SaveCaseTypeRequestBody {
    casenumber: string;
    caseTypeIntersects: {
        casetypesourceid: string;
        isprimary: boolean;
        statuscode: number;
    }[];
}

export interface WorksheetListByCaseType {
    [x: string]: { worksheetId: string; displayName: string }[];
}

export interface WorksheetGroupResponse {
    groupDescription: string;
    groupName: string;
    worksheetCaseGroupId: string;
    worksheetTitles: {
        worksheetId: string;
        displayName: string;
    }[];
}

export interface WorksheetElementAnswers extends WorksheetAnswerElement {
    asset?: WorksheetAnswerAsset;
}

// export interface WorksheetGridCellAnswer {
//     worksheetElementGridId: string;
//     value: string;
// }

export interface WorksheetAnswerElement {
    elementId: string;
    selectedOptions: SelectedOption[];
    worksheetGridCellAnswers?: WorksheetGridCellAnswer[];
    sourceId?: string;
    elementOrder?: string;
}

export interface WorksheetAnswerAsset {
    answerFile: FormElementFile;
}

export interface WorksheetAnswerAssetBody {
    assetTypeId: WorksheetAssetType;
    assetDispositionId: 'TECHSHARE';
    caseNumber: string;
    fileName: string;
    restricted?: boolean;
    hasOverlay: boolean;
    name?: string;
    note: string;
    replacesAssetId?: string;
    worksheetQuestionSubmissionSourceId: string;
}

export interface WorksheetUpdateAnswerAsset {
    assetTypeId: WorksheetAssetType.IMAGE;
    assetDispositionId: 'TECHSHARE';
    caseNumber: string;
    name: string;
    fileName: string;
    restricted?: boolean;
    note: string;
    hasOverlay: boolean;
    replacesAssetId: string;
}

export interface CreateAnswerAssetsBody {
    assets: (WorksheetAnswerAssetBody | WorksheetUpdateAnswerAsset)[];
    techlinecasestatus: string;
}

/**
 * @deprecated Use {@link WorksheetSubmissionEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface WorksheetAnswerBody {
    submissionId: string;
    caseNumber: string;
    userName: string;
    worksheetId: string;
    answers: WorksheetAnswerElement[];
    status: WorksheetAnswerStatus;
}

export interface WorksheetSubmissionResponse {
    statusCode: number;
    message: string;
    status: string;
    results: {
        retailerId: string;
        lastUpdatedTimestamp: string;
        lastUpdatedTimestampForDisplay: string;
        submissionId: string;
        caseNumber: string;
        userName: string;
        worksheetId: string;
        answers: WorksheetAnswerElement[];
        status: WorksheetAnswerStatus;
        statusCode: number;
    };
}

export interface DeleteDraftWorksheetSubmissionResponse {
    statusCode: number;
    status: string;
    message: string;
    results: string;
}

/**
 * @deprecated Use {@link WorksheetSubmissionEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface WorksheetSubmissions {
    lastUpdatedTimestamp: string;
    lastUpdatedTimestampForDisplay: string;
    retailerNumber: string;
    worksheetCaseType: string;
    worksheetCaseTypeSourceId: string;
    status: WorksheetAnswerStatus;
    statusCode: string;
    submissionId: string;
    userDisplayName: string;
    userName: string;
    worksheetId: string;
    worksheetTitle: string;
}

/**
 * @deprecated Use {@link WorksheetCaseGroupEntity} from `@packages/domain`. Replaced by domain entity layer.
 * Retained until all consumers are migrated.
 */
export interface WorksheetCaseGroup {
    worksheetCaseGroupId: string;
    groupName: string;
}
export type CidCvnWorksheetTitles = {
    displayName: string;
    worksheetId: string;
};
